import { useMemo } from 'react';

export type SponsorFeedType = 'story' | 'milestone' | 'digital_good' | 'press';

export interface SponsorFeedEntry {
  id: string;
  type: SponsorFeedType;
  title: string;
  summary?: string | null;
  media_url?: string | null;
  cta_text?: string | null;
  cta_url?: string | null;
  tag?: string | null;
  published_at?: string | null;
}

interface ProfileFeedEditorProps {
  entries: SponsorFeedEntry[];
  onChange: (entries: SponsorFeedEntry[]) => void;
}

function createEntry(type: SponsorFeedType): SponsorFeedEntry {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `feed-${Date.now()}`;
  return {
    id,
    type,
    title: '',
    summary: '',
    media_url: '',
    cta_text: '',
    cta_url: '',
    tag: type === 'milestone' ? 'Milestone' : '',
  };
}

function updateEntry(entries: SponsorFeedEntry[], id: string, updates: Partial<SponsorFeedEntry>) {
  return entries.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry));
}

const FEED_TYPES: { value: SponsorFeedType; label: string }[] = [
  { value: 'story', label: 'Story update' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'press', label: 'Press mention' },
];

export default function ProfileFeedEditor({ entries, onChange }: ProfileFeedEditorProps) {
  const list = useMemo(() => entries || [], [entries]);
  const goods = list.filter((entry) => entry.type === 'digital_good');
  const feed = list.filter((entry) => entry.type !== 'digital_good');

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <header className="space-y-1">
          <h3 className="text-lg font-semibold text-cocoa">Digital goods gallery</h3>
          <p className="text-sm text-cocoa/70">Showcase badges, avatars, and keepsakes tied to your sponsorship.</p>
        </header>
        <div className="space-y-3">
          {goods.map((entry) => (
            <div key={entry.id} className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-4">
              <label className="text-sm font-medium text-cocoa">
                Title
                <input
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  value={entry.title}
                  onChange={(event) => onChange(updateEntry(list, entry.id, { title: event.target.value }))}
                  placeholder="Classroom art drop"
                />
              </label>
              <label className="text-sm font-medium text-cocoa">
                Media URL
                <input
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  value={entry.media_url || ''}
                  onChange={(event) => onChange(updateEntry(list, entry.id, { media_url: event.target.value }))}
                  placeholder="https://example.com/preview.png"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-medium text-cocoa">
                  CTA label
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.cta_text || ''}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { cta_text: event.target.value }))}
                    placeholder="View bundle"
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  CTA URL
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.cta_url || ''}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { cta_url: event.target.value }))}
                    placeholder="https://example.com/checkout"
                  />
                </label>
              </div>
              <div className="flex justify-between text-sm text-cocoa/70">
                <span>Optional: set published date in the story feed.</span>
                <button
                  type="button"
                  className="font-medium text-brick"
                  onClick={() => onChange(list.filter((item) => item.id !== entry.id))}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa"
          onClick={() => onChange([...list, createEntry('digital_good')])}
        >
          Add digital good
        </button>
      </section>

      <section className="space-y-3">
        <header className="space-y-1">
          <h3 className="text-lg font-semibold text-cocoa">Profile feed</h3>
          <p className="text-sm text-cocoa/70">Updates appear in chronological order beneath your quote.</p>
        </header>
        <div className="space-y-3">
          {feed.map((entry) => (
            <div key={entry.id} className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm font-medium text-cocoa">
                  Type
                  <select
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.type}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { type: event.target.value as SponsorFeedType }))}
                  >
                    {FEED_TYPES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="sm:col-span-2 text-sm font-medium text-cocoa">
                  Title
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.title}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { title: event.target.value }))}
                    placeholder="Slot 5 funded!"
                  />
                </label>
              </div>
              <label className="text-sm font-medium text-cocoa">
                Summary
                <textarea
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  rows={3}
                  value={entry.summary || ''}
                  onChange={(event) => onChange(updateEntry(list, entry.id, { summary: event.target.value }))}
                  placeholder="Share context for this milestone or update."
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-medium text-cocoa">
                  CTA label
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.cta_text || ''}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { cta_text: event.target.value }))}
                    placeholder="Read update"
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  CTA URL
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.cta_url || ''}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { cta_url: event.target.value }))}
                    placeholder="https://example.com/story"
                  />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-medium text-cocoa">
                  Image URL (optional)
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.media_url || ''}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { media_url: event.target.value }))}
                    placeholder="https://example.com/photo.jpg"
                  />
                </label>
                <label className="text-sm font-medium text-cocoa">
                  Tag / Pill label
                  <input
                    className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.tag || ''}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { tag: event.target.value }))}
                    placeholder="Milestone"
                  />
                </label>
              </div>
              <div className="flex justify-between text-sm text-cocoa/70">
                <span>
                  Optional ISO date (YYYY-MM-DD) controls ordering when published.
                </span>
                <div className="flex items-center gap-2">
                  <input
                    className="w-40 rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                    value={entry.published_at || ''}
                    onChange={(event) => onChange(updateEntry(list, entry.id, { published_at: event.target.value }))}
                    placeholder="2025-03-01"
                  />
                  <button
                    type="button"
                    className="font-medium text-brick"
                    onClick={() => onChange(list.filter((item) => item.id !== entry.id))}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa"
          onClick={() => onChange([...list, createEntry('story')])}
        >
          Add feed entry
        </button>
      </section>
    </div>
  );
}
